import { createSlice } from "@reduxjs/toolkit";

import LessonContentModel from "../models/LessonContentModel";
import ActionButtonModel from "../models/ActionButtonModel";

import { COURSE, LESSON } from "../Helpers/Enums";
import { ShuffleArray } from "../Helpers/Utilities";

const {
  PARAGRAPH,
  QUOTE,
  VIDEO,
  SINGLE_CHOICE_QUIZ,
  MULTIPLE_CHOICE_QUIZ,
  ACTION_BUTTON,
  IMAGE,
  SORTING_GAME,
  MATCHING_GAME,
  SCORM,
  DOCUMENT,
  MEETING,
} = LESSON.CONTENT_TYPE;

const { LESSON_WITH_SUBLESSON, ASSESSMENT_LESSON } = LESSON.TYPE;

const initialState = {
  courses: [],
  currentCourse: {},
  activeLessonIndex: -1,
  activeSubLessonIndex: -1,
  currentCourseLessons: [],
  isFirstLoad: true,
  selectedCourseCategory: [],
  selectedCouseFilter: COURSE.STATUS.ASSIGNED_COURSES,
  previousLessonCompleteIndex: -1,
  isLastLessonOfCourse: false,
  courseCreatedOn: "",
  isCourseExpired: false,
};

const GetLessonContent = (
  lessonContents,
  isMeeting = false,
  isAssessment = false,
) => {
  const lastContent = lessonContents.at(-1);

  const isLastContentButton =
    lessonContents.length !== 0 && lastContent?.contentTypeId === ACTION_BUTTON;

  let newLessonContents = lessonContents.slice();

  if (!isLastContentButton && !isMeeting && !isAssessment) {
    const lessonContent = new LessonContentModel();
    lessonContent.lessonContentId = lastContent.lessonContentId + 1 || 1;
    lessonContent.contentTypeId = ACTION_BUTTON;
    lessonContent.contentOrder = lastContent?.contentOrder + 1 || 1;
    lessonContent.actionButtonContent = new ActionButtonModel(
      lastContent.lessonContentId + 1 || 1,
      "CONTINUE",
    );

    const newLC = LessonContentModel.convertToJSObject(lessonContent);

    newLessonContents = [
      ...newLessonContents,
      LessonContentModel.convertToJSObject(newLC),
    ];
  }

  return newLessonContents.map((item) => {
    const { lessonContentId, contentOrder, contentTypeId, quiz } = item;

    const updatedLessonContent = {
      lessonContentId,
      order: contentOrder,
      isActive: false,
      isCompleted: false,
      isFocused: false,
    };

    switch (contentTypeId) {
      case PARAGRAPH: {
        const { editorTextContent } = item;

        return {
          ...updatedLessonContent,
          contentType: PARAGRAPH,
          editorData: editorTextContent.content,
        };
      }

      case QUOTE: {
        const { quoteContent } = item;
        return {
          ...updatedLessonContent,
          contentType: QUOTE,
          quoteData: {
            text: quoteContent.quoteText,
            by: quoteContent.quoteBy,
          },
        };
      }

      case VIDEO: {
        return {
          ...updatedLessonContent,
          contentType: VIDEO,
          videoData: {
            ...item.videoContent,
          },
        };
      }

      case MULTIPLE_CHOICE_QUIZ:
      case SINGLE_CHOICE_QUIZ:
        return {
          ...updatedLessonContent,
          contentType: contentTypeId,
          quiz: quiz.map((questionItem, index) => {
            const {
              questionId,
              isAnswered,
              questionText,
              questionImage,
              questionTypeId,
              answers,
            } = questionItem;
            return {
              id: questionId,
              isActive: isAnswered || index === 0,
              isPrevious: false,
              answers: answers.map((answerItem) => {
                const { answerId, answerText, isValid, isSelected } =
                  answerItem;
                return {
                  id: answerId,
                  answerText,
                  isValid,
                  isSelected,
                };
              }),
              questionText,
              questionImage,
              questionTypeId,
              isAnswered,
            };
          }),
        };

      case ACTION_BUTTON: {
        const { actionButtonContent } = item;
        return {
          ...updatedLessonContent,
          contentType: ACTION_BUTTON,
          actionButtonText: actionButtonContent.title,
        };
      }

      case IMAGE: {
        const { imageContent } = item;
        return {
          ...updatedLessonContent,
          contentType: contentTypeId,
          imageContent,
        };
      }

      case SORTING_GAME: {
        const { sortingGame } = item;

        return {
          ...updatedLessonContent,
          contentType: contentTypeId,
          sortingGame: {
            ...sortingGame,
            shuffledChoices: ShuffleArray(
              sortingGame.sortingGames.reduce(
                (items, cur) => [...items, ...cur.items],
                [],
              ),
            ),
          },
        };
      }

      case MATCHING_GAME: {
        const { matchingGame } = item;

        return {
          ...updatedLessonContent,
          contentType: contentTypeId,
          matchingGame,
        };
      }

      case SCORM: {
        const { scormLaunchParameters } = item;

        return {
          ...updatedLessonContent,
          contentType: contentTypeId,
          scormLaunchParameters,
        };
      }

      case DOCUMENT: {
        return {
          ...updatedLessonContent,
          contentType: DOCUMENT,
          documentData: {
            ...item.documentContent,
          },
        };
      }

      case MEETING: {
        const { meetingContent } = item;

        return {
          ...updatedLessonContent,
          contentType: MEETING,
          meetingContent,
        };
      }

      default:
        return {};
    }
  });
};

const GetUpdatedLessonContent = (lessonContents, lessonContentIndex) => {
  // If lesson content index is not set then make the first lesson content as active
  if (lessonContentIndex === undefined) {
    return lessonContents.map((item, index) => {
      if (index === 0) {
        return {
          ...item,
          isActive: true,
          isFocused: true,
        };
      }

      return {
        ...item,
        isActive: false,
        isFocused: false,
      };
    });
  }

  const updatedLessonContents = [];
  let actionButtonActivatedCount = 0;
  let itemFocused = 0;

  for (let index = 0; index < lessonContents.length; index++) {
    const data = lessonContents[index];

    if (index >= lessonContentIndex && actionButtonActivatedCount < 1) {
      // Activate those content which are after the current content index
      if (index === lessonContentIndex) {
        // if (
        //   [
        //     ACTION_BUTTON,
        //     MULTIPLE_CHOICE_QUIZ,
        //     SINGLE_CHOICE_QUIZ,
        //     SORTING_GAME,
        //     MATCHING_GAME,
        //   ].includes(data.contentType) &&
        //   !data.isActive
        // ) {
        //   actionButtonActivatedCount += 1;
        // }
        updatedLessonContents.push({
          ...data,
          isActive: true,
          isFocused: false,
        });
      } else if (
        index > lessonContentIndex &&
        [ACTION_BUTTON, MULTIPLE_CHOICE_QUIZ, SINGLE_CHOICE_QUIZ].includes(
          data.contentType,
        )
      ) {
        //If the content is Action button or Quiz then increment the actionButtonActivatedCount
        //so that no other content after this is made active as the user needs to perform the action button click
        //or has to complete the quiz
        actionButtonActivatedCount += 1;
        updatedLessonContents.push({
          ...data,
          isActive: true,
          isFocused: itemFocused === 0,
        });
        itemFocused = 1;
      } else {
        updatedLessonContents.push({
          ...data,
          isActive: true,
          isFocused: itemFocused === 0,
        });
        itemFocused = 1;
      }
    } else {
      updatedLessonContents.push({
        ...data,
      });
    }
  }

  return updatedLessonContents;
};

const courseSlice = createSlice({
  initialState,
  name: "course",
  reducers: {
    ResetCourseState(state) {
      state.currentCourse = {};
      state.currentCourseLessons = [];
      state.activeLessonIndex = -1;
      state.activeSubLessonIndex = -1;
    },

    SetSelectedCourseCategory(state, action) {
      const courseCategory = action.payload;
      state.selectedCourseCategory = courseCategory ? [courseCategory] : [];
    },

    /* <summary>
    description: Sets the selected course filter state from the action payload
    <summary>*/
    SetSelectedCourseFilter(state, action) {
      const courseFilter = action.payload;
      state.selectedCouseFilter =
        courseFilter || COURSE.STATUS.ASSIGNED_COURSES;
    },

    /* <summary>
    date: 25-11-2022
    Name: GM
    description: Sets selected lesson as active and updates remaining lesson data too
    <summary>*/
    SetSelectedLessonAsActive(state, action) {
      const newLessonIndex = action.payload;

      const isSelectedLessonLastViewedLesson =
        newLessonIndex === state.activeLessonIndex;

      const updatedLessons = state.currentCourseLessons.map((item, index) => {
        /* Set this lesson as active and its YOffset to page height so that 
           when it loads it looks as if page is coming up from bottom  */
        if (index === newLessonIndex) {
          return {
            ...item,
            isActive: true,
            lessonYOffset: window.innerHeight,
          };
        }

        /* Set all lessons before newLessonIndex as inactive and set their YOffset 
          to a value that makes them looks as if page is droping down when they load */
        if (index < newLessonIndex) {
          return { ...item, isActive: false, lessonYOffset: -300 };
        }

        // Set all lessons after newLessonIndex as inactive
        return { ...item, isActive: false };
      });

      state.currentCourseLessons = updatedLessons;
      state.activeLessonIndex = newLessonIndex;

      if (!isSelectedLessonLastViewedLesson) {
        state.activeSubLessonIndex = 0;
      }
    },

    /* <summary>
    date: 25-11-2022
    Name: GM
    description: Set selected course as active and makes remaining courses inactive
    <summary>*/
    SetCourseToActive(state, action) {
      const coursePayload = action.payload;
      const updatedCourses = state.courses.map((item) => {
        if (item.courseId === coursePayload.courseId) {
          return { ...item, isActive: true };
        }

        return { ...item, isActive: false };
      });

      state.currentCourse = {
        ...coursePayload,
        courseId: coursePayload.courseId,
        courseName: "",
        courseDescription: "",
        autherName: "",
        lessons: [],
        courseGuid: "",
        courseProgressPercent: 0,
        courseFeatureImage: "",
        shortDescription: "",
        courseCreatedOn: coursePayload.courseCreatedOn,
        isCourseExpired: coursePayload.isCourseExpired,
      };

      state.courses = updatedCourses;
    },

    SetPreviousLessonIndex(state, action) {
      const { lessonIndex } = action.payload;
      state.previousLessonCompleteIndex = lessonIndex;
    },

    /* <summary>
    date: 25-11-2022
    Name: GM
    description: Navigate to lesson 
    <summary>*/
    NavigateToOtherLesson(state, action) {
      const { lessonIndex, isLessonCompleted } = action.payload;
      const allCourseLessonsLength = state.currentCourseLessons.length;

      const updatedLessons = state.currentCourseLessons.map((item, index) => {
        // Set the next lesson to be active
        if (index === lessonIndex) {
          return {
            ...item,
            isActive: true,
            isFocused: false,
          };
        }

        // Update the current lesson
        if (isLessonCompleted && index === lessonIndex - 1) {
          return {
            ...item,
            isActive: false,
            lessonYOffset: -300,
            isFocused: false,
          };
        }

        if (index < lessonIndex) {
          return {
            ...item,
            isActive: false,
            lessonYOffset: -300,
            isFocused: false,
          };
        }

        return {
          ...item,
          isActive: false,
          lessonYOffset: window.innerHeight / 2,
          isFocused: false,
        };
      });

      state.activeLessonIndex =
        lessonIndex === allCourseLessonsLength ? 0 : lessonIndex;
      state.activeSubLessonIndex = 0;

      state.currentCourseLessons = updatedLessons;
    },

    /* <summary>
    date: 25-11-2022
    Name: GM
    description: Navigate to sub lesson 
    <summary>*/
    NavigateToSubLesson(state, action) {
      const { subLessonIndex } = action.payload;
      state.activeSubLessonIndex = subLessonIndex;
    },

    /* <summary>
    description: Updates the lesson content to be displayed of a course based on lesson type
    <summary>*/
    UpdateLessonContent(state, action) {
      const lessonContentIndex = action.payload;
      const lesson = state.currentCourseLessons[state.activeLessonIndex];

      if (lesson.lessonType === LESSON_WITH_SUBLESSON) {
        const _lessonContent =
          lesson.subLessons[state.activeSubLessonIndex].lessonContent;
        const updatedLessonContent = GetUpdatedLessonContent(
          _lessonContent,
          lessonContentIndex,
        );

        state.currentCourseLessons[state.activeLessonIndex].subLessons[
          state.activeSubLessonIndex
        ].lessonContent = [...updatedLessonContent];
      } else {
        const _lessonContent = lesson.lessonContent;
        const updatedLessonContent = GetUpdatedLessonContent(
          _lessonContent,
          lessonContentIndex,
        );

        state.currentCourseLessons[state.activeLessonIndex].lessonContent = [
          ...updatedLessonContent,
        ];
      }
    },

    UpdateVideoCipherData(state, action) {
      const { contentOrderId, otp, playbackInfo } = action.payload;
      const { currentCourseLessons, activeLessonIndex, activeSubLessonIndex } =
        state;

      const lesson = currentCourseLessons[activeLessonIndex];

      if (lesson.lessonType === LESSON_WITH_SUBLESSON) {
        const lessonContent = lesson.subLessons[
          activeSubLessonIndex
        ].lessonContent.find((t) => t.order === contentOrderId);

        if (lessonContent.contentType === VIDEO) {
          const contentIndex = lesson.subLessons[
            activeSubLessonIndex
          ].lessonContent.findIndex((t) => t.order === contentOrderId);

          const oldData =
            currentCourseLessons[activeLessonIndex].subLessons[
              activeSubLessonIndex
            ].lessonContent[contentIndex].videoData;

          currentCourseLessons[activeLessonIndex].subLessons[
            activeSubLessonIndex
          ].lessonContent[contentIndex].videoData = {
            ...oldData,
            videoCiperOtp: otp,
            videoCipherPlaybackInfo: playbackInfo,
          };
        }
      } else {
        const lessonContent = lesson.lessonContent.find(
          (t) => t.order === contentOrderId,
        );

        if (lessonContent.contentType === VIDEO) {
          const contentIndex = lesson.lessonContent.findIndex(
            (t) => t.order === contentOrderId,
          );
          const oldData =
            currentCourseLessons[activeLessonIndex].lessonContent[contentIndex]
              .videoData;

          currentCourseLessons[activeLessonIndex].lessonContent[
            contentIndex
          ].videoData = {
            ...oldData,
            videoCiperOtp: otp,
            videoCipherPlaybackInfo: playbackInfo,
          };
        }
      }
    },

    /* <summary>
    description: Sets the currentCourse and currentCourseLessons state with data from the course payload
    <summary>*/
    SetCourseDetails(state, action) {
      const { payload: coursePayload } = action;

      if (coursePayload.courseId === state.currentCourse.courseId) {
        return;
      }

      const {
        courseId,
        courseName,
        courseCode,
        courseDescription,
        autherName,
        lessons,
        courseGuid,
        courseFeatureImage,
        isCourseAssignedToUser,
        courseProgressPercent,
        courseCompletedOn,
        lastViewedLessonId,
        lastViewedSubLessonId,
        courseTypeId,
        avgCourseRating,
        totalCourseReviews,
        shortDescription,
        courseAssignedOn,
        courseStartDate,
        allowAccess,
        liveCourseStartDate,
        liveCourseEndDate,
        liveCourseMeetingLink,
        categories,
        assessmentAnswered,
        isStart,
        isHtmlCourse,
      } = coursePayload;

      state.currentCourse = {
        ...state.currentCourse,
        courseTypeId,
        courseId,
        courseName,
        courseCode,
        courseDescription,
        autherName,
        lessons,
        courseGuid,
        courseFeatureImage,
        isCourseAssignedToUser,
        courseProgressPercent,
        courseCompletedOn,
        avgCourseRating,
        totalCourseReviews,
        shortDescription,
        courseAssignedOn,
        courseStartDate,
        allowAccess,
        liveCourseStartDate,
        liveCourseEndDate,
        liveCourseMeetingLink,
        categories,
        assessmentAnswered,
        isHtmlCourse,
      };

      // Calculate Last Viewed Lesson & SubLesson indexes
      const lastViewedLessonIndex = isStart
        ? 0
        : lessons.findIndex((l) => l.lessonId === lastViewedLessonId);

      const lastViewedLesson = lessons[lastViewedLessonIndex];

      let lastViewedSubLessonIndex = 0;

      if (!!lastViewedLesson && lastViewedLesson.subLessons.length > 0) {
        lastViewedSubLessonIndex = lastViewedLesson.subLessons.findIndex(
          (sl) => sl.subLessonId === lastViewedSubLessonId,
        );
      }

      const resolveToCorrectIndex = (index) => (index !== -1 ? index : 0);

      state.currentCourseLessons = lessons.map((item, index) => {
        const {
          lessonId,
          lessonName,
          lessonType,
          contentViewedPercent,
          contentInteractedPercent,
          enabled,
          isCompleted,
          progress,
          subLessons,
        } = item;

        const updatedLesson = {
          lessonId,
          lessonName,
          lessonType,
          lessonScrollProgressValue: contentViewedPercent,
          contentCompletionValue: contentInteractedPercent,
          lessonContent: [],
          isScrolled: false,
          enabled,
          isCompleted,
          progress,
          isHtmlCourse: item.isHtmlCourse,
          subLessons: subLessons.map((sl) => {
            const {
              subLessonId,
              subLessonName,
              subLessonOrderIndex,
              enabled,
              isCompleted,
              progress,
            } = sl;
            return {
              subLessonId,
              subLessonName,
              subLessonOrderIndex,
              lessonContent: [],
              isScrolled: false,
              enabled,
              isCompleted,
              progress,
            };
          }),
        };

        if (index === resolveToCorrectIndex(lastViewedLessonIndex)) {
          return {
            ...updatedLesson,
            isActive: true,
            lessonYOffset: -200,
          };
        }

        return {
          ...updatedLesson,
          isActive: false,
          lessonYOffset: window.innerHeight,
        };
      });

      state.activeLessonIndex = resolveToCorrectIndex(lastViewedLessonIndex);
      state.activeSubLessonIndex = resolveToCorrectIndex(
        lastViewedSubLessonIndex,
      );
    },

    SetCourseAssessmentAnswered(state, action) {
      state.currentCourse.assessmentAnswered = action.payload;
    },

    /* <summary>
    description: Sets the lesson contents of the active course lesson
    <summary>*/
    SetLessonsContent(state, action) {
      const lessonPayload = action.payload;

      const isAssessmentLesson =
        state.currentCourseLessons[state.activeLessonIndex]?.lessonType ===
        ASSESSMENT_LESSON;

      const isMeeting =
        lessonPayload?.lessonContent?.[0]?.contentTypeId ===
        LESSON.CONTENT_TYPE.MEETING;

      state.currentCourseLessons[state.activeLessonIndex].lessonContent =
        GetLessonContent(
          lessonPayload.lessonContent,
          isMeeting,
          isAssessmentLesson,
        );
    },

    /* <summary>
    description: Sets the active course's total reviews and avg. rating data
    <summary>*/
    SetCourseReviewStats(state, action) {
      const { totalReviews, averageRating } = action.payload;
      state.currentCourse.totalCourseReviews = totalReviews;
      state.currentCourse.avgCourseRating = averageRating;
    },

    /* <summary>
    description: Sets the lesson contents of the sub lesson of the active course lesson
    <summary>*/
    SetSubLessonsContent(state, action) {
      const lessonPayload = action.payload;
      const { currentCourseLessons, activeLessonIndex } = state;

      const isMeeting =
        lessonPayload?.lessonContent?.[0]?.contentTypeId ===
        LESSON.CONTENT_TYPE.MEETING;

      currentCourseLessons[activeLessonIndex].subLessons = currentCourseLessons[
        activeLessonIndex
      ].subLessons.map((item) => {
        if (item.subLessonId !== lessonPayload.subLessonId) {
          return { ...item };
        }

        return {
          ...item,
          lessonContent: GetLessonContent(
            lessonPayload.lessonContent,
            isMeeting,
          ),
        };
      });
    },

    SetOnContentFirsLoad(state, action) {
      state.isFirstLoad = action.payload;
    },

    SetIsLastLessonOfCourse(state, action) {
      state.isLastLessonOfCourse = action.payload;
    },

    EnableCourseLessons(state) {
      state.currentCourseLessons = state.currentCourseLessons.map((item) => {
        const updatedLesson = {
          ...item,
          enabled: true,
          subLessons: item.subLessons.map((sl) => {
            return {
              ...sl,
              enabled: true,
            };
          }),
        };

        return updatedLesson;
      });
    },

    AssignCourseToUser(state) {
      state.currentCourse.isCourseAssignedToUser = true;
    },

    UpdateLessonProgress(state, action) {
      const { lessonId, isCompleted } = action.payload;

      let updated = state.currentCourseLessons.map((l) => {
        if (l.lessonId !== lessonId) {
          return l;
        }

        return {
          ...l,
          isCompleted,
          progress: isCompleted ? 100 : 0,
        };
      });

      // Check whether all non assessment are completed
      const lessonsComplete = updated
        .filter((l) => l.lessonType !== ASSESSMENT_LESSON)
        .every((l) => l.isCompleted);

      if (lessonsComplete) {
        updated = updated.map((l) =>
          l.lessonType !== ASSESSMENT_LESSON
            ? l
            : {
                ...l,
                enabled: true,
              },
        );
      }

      state.currentCourseLessons = updated;
    },

    UpdateSubLessonProgress(state, action) {
      const { lessonId, subLessonId, isCompleted } = action.payload;

      let updated = state.currentCourseLessons.map((l) => {
        if (l.lessonId !== lessonId) {
          return l;
        }

        const updatedSls = l.subLessons.map((sl) => {
          if (sl.subLessonId !== subLessonId) {
            return sl;
          }

          return {
            ...sl,
            isCompleted,
            progress: isCompleted ? 100 : 0,
          };
        });

        const allSubLessonComplete = updatedSls.every((sl) => sl.isCompleted);
        const updatedLessonProgress =
          (updatedSls.filter((sl) => sl.isCompleted).length /
            updatedSls.length) *
          100;

        return {
          ...l,
          isCompleted: allSubLessonComplete,
          progress: updatedLessonProgress,
          subLessons: updatedSls,
        };
      });

      // Check whether all non assessment are completed
      const lessonsComplete = updated
        .filter((l) => l.lessonType !== ASSESSMENT_LESSON)
        .every((l) => l.isCompleted);

      if (lessonsComplete) {
        updated = updated.map((l) =>
          l.lessonType !== ASSESSMENT_LESSON
            ? l
            : {
                ...l,
                enabled: true,
              },
        );
      }

      state.currentCourseLessons = updated;
    },
  },
});

export const {
  ResetCourseState,
  SetCourseToActive,
  SetSelectedLessonAsActive,
  SetPreviousLessonIndex,
  SetLessonsContent,
  SetSelectedCourseCategory,
  SetSelectedCourseFilter,
  SetCourseAssessmentAnswered,

  UpdateLessonContent,
  UpdateVideoCipherData,
  UpdateLessonProgress,
  UpdateSubLessonProgress,

  SetCourseDetails,
  SetCourseReviewStats,
  SetSubLessonsContent,
  SetOnContentFirsLoad,
  SetIsLastLessonOfCourse,

  NavigateToOtherLesson,
  NavigateToSubLesson,

  EnableCourseLessons,
  AssignCourseToUser,
} = courseSlice.actions;

export default courseSlice.reducer;
